import React from "react";
import { Pagination, Stack, useMediaQuery } from "@mui/material";
import { usePagination } from "react-instantsearch";
import { theme } from "../../theme";
import { customPaginationStyles } from "../../pages/styles";

/**
 * Custom Pagination Component for search results using Material-UI and react-instantsearch.
 *
 * This component provides a responsive pagination interface that adapts to different screen sizes.
 * It handles the conversion between Algolia's 0-based indexing and Material-UI's 1-based indexing.
 *
 * Features:
 * - Responsive design with different pagination behavior on mobile vs desktop
 * - Automatic hiding when there's only one page of results
 * - Integration with react-instantsearch for seamless search pagination
 * - Custom styling through the customPaginationStyles theme
 *
 * @returns {JSX.Element | null} The pagination component or null if only one page exists
 */
const CustomPagination: React.FC = () => {
  const { currentRefinement, refine, nbPages } = usePagination();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  /**
   * Handles page change events from the Material-UI Pagination component.
   * Converts the 1-based page number from MUI to 0-based indexing for Algolia.
   *
   * @param event - The change event from the pagination component
   * @param page - The new page number (1-based)
   */
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    refine(page - 1);
  };

  if (nbPages <= 1) {
    return null;
  }

  return (
    <Stack
      spacing={customPaginationStyles.container.spacing}
      alignItems={customPaginationStyles.container.alignItems}
    >
      <Pagination
        count={nbPages}
        page={currentRefinement + 1}
        onChange={handlePageChange}
        siblingCount={isMobile ? 0 : 2}
        boundaryCount={1}
        sx={customPaginationStyles.pagination(theme)}
      />
    </Stack>
  );
};

export default CustomPagination;
